import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpInternalService } from './http.service';

export type ContactKind = 'supplier' | 'customer';
export type ContactStatus = 'active' | 'archived';

export interface ContactListItem {
  contactId: string;
  name: string;
  contactPerson?: string;
  email?: string;
  taxNumber?: string;
  hexColorClass?: string;
  isArchived: boolean;
  isSupplier: boolean;
  isCustomer: boolean;
  website?: string;
}

export interface ContactList {
  contacts: ContactListItem[];
  total: number;
  activeCount: number;
  archivedCount: number;
}

/** A contact's website, and the master contact (with its logo) it matched. */
export interface ContactWebsite {
  website: string | null;
  masterContactId?: string | null;
  masterContactName?: string | null;
  logoUrl?: string | null;
  /** linked | created | pending | unchanged | unsupported | cleared */
  outcome?: string;
  message?: string;
}

export interface ContactMergeResult {
  succeeded: boolean;
  documentsMoved: number;
  documentsLeft: number;
  archivedInBooks: boolean;
  syncedRecordsMoved: number;
  notes: string[];
}

/** Settings › Lists › Contacts: api/contacts. */
@Injectable({
  providedIn: 'root'
})
export class ContactsService {
  constructor(private _http: HttpInternalService) { }

  list(organizationId: string, kind: ContactKind, status: ContactStatus, search: string, page: number, pageSize: number): Observable<ContactList> {
    return this._http.getRequest<ContactList>('contacts', {
      organizationId, kind, status, search: search || '', page, pageSize
    });
  }

  getWebsite(organizationId: string, kind: ContactKind, contactId: string): Observable<ContactWebsite> {
    return this._http.getRequest<ContactWebsite>(`contacts/${encodeURIComponent(contactId)}/website`, { organizationId, kind })
      .pipe(map(w => this.absolute(w)));
  }

  /** Saves the website; the API matches the contact to a master contact by it and fetches the logo. */
  setWebsite(organizationId: string, kind: ContactKind, contactId: string, website: string): Observable<ContactWebsite> {
    return this._http.putRequest<ContactWebsite>(`contacts/${encodeURIComponent(contactId)}/website`, { organizationId, kind, website })
      .pipe(map(w => this.absolute(w)));
  }

  merge(organizationId: string, kind: ContactKind, keepContactId: string, mergeContactId: string): Observable<ContactMergeResult> {
    return this._http.postRequest<ContactMergeResult>('contacts/merge', { organizationId, kind, keepContactId, mergeContactId });
  }

  // The API returns "/api/master-contacts/{id}/logo": point it at the API host, not the page's.
  private absolute(w: ContactWebsite): ContactWebsite {
    return w && w.logoUrl ? { ...w, logoUrl: this._http.apiUrl(w.logoUrl) } : w;
  }
}
